import { QueryClient, useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { ErrorResponse } from "react-router-dom";
import {
  ApplyLeaveForm,
  ClassAssignmentConflictPreview,
  EmployeeManagementReactivationPreview,
  LeaveConflictPreview,
  LeavePreviewForm,
  LeaveRemovalResult,
  RuleRemovalResult,
  SetStaffClassTypesForm,
  SetWorkingHoursForm,
  WorkingHoursConflictPreview,
} from "shared/types";
import { getApiSuccessMessage, getApiErrorMessage } from "shared/utils";
import client from "shared/utils/axios-config";
import { translate, translateWithVars } from "shared/languages";
import { invalidateEmployeeLists } from "hooks/mutations/employeeLists";
import { invalidateCustomerList } from "hooks/mutations/customerList";

// The Instruktur list's roster summary (working days / upcoming leave /
// assigned classes column, getAllStaffByBusinessId's with_summary=true) is
// cached under the list's own query key, entirely separate from
// ["user", staff_id], which only backs the detail page. The Karyawan pages
// read the list under KARYAWAN_STAFF_TYPE, so the shared helper refreshes
// every employee-list key, not just [UserType.Instructor]: refreshing only
// that one left the Instruktur page showing the old "Semua" chip for five
// minutes after an assigned-classes save.

// Plain read, not a useMutation: no success/error toast, no cache
// invalidation, purely a "who would this affect" check consumed directly by
// useLeavePreview (LeaveConflictWarningModal.tsx), the same style
// checkScheduleClashesV2 already uses for the equivalent schedule-clash gate.
const getLeaveConflictPreview = async (
  form: LeavePreviewForm,
): Promise<LeaveConflictPreview> => {
  const res = await client.post(`/v1/staff/leave/preview`, form);
  return res.data.data;
};

// Plain read, same style as getLeaveConflictPreview above: no body needed,
// the endpoint always scans against whatever working_hour/leave data is
// CURRENTLY stored for every instructor in the caller's business. Consumed by
// useReactivationPreview (EmployeeManagementReactivationWarningModal.tsx).
const getEmployeeManagementReactivationPreview =
  async (): Promise<EmployeeManagementReactivationPreview> => {
    const res = await client.post(`/v1/staff/employee-management/reactivation-preview`);
    return res.data.data;
  };

// Plain reads, same style as getLeaveConflictPreview above — each checks a
// PROPOSED (not-yet-saved) change against that one instructor's near-future
// schedule, gating the real setWorkingHours/setStaffClassTypes mutation via
// useWorkingHoursPreview/useClassAssignmentPreview
// (WorkingHoursConflictWarningModal.tsx / ClassAssignmentConflictWarningModal.tsx).
// Reuse the exact SetWorkingHoursForm/SetStaffClassTypesForm shapes the real
// save mutations already take — the preview body is identical to what would
// actually be saved.
const getWorkingHoursConflictPreview = async (
  form: SetWorkingHoursForm,
): Promise<WorkingHoursConflictPreview> => {
  const res = await client.post(`/v1/staff/working-hours/preview`, form);
  return res.data.data;
};

const getClassAssignmentConflictPreview = async (
  form: SetStaffClassTypesForm,
): Promise<ClassAssignmentConflictPreview> => {
  const res = await client.post(`/v1/staff/class-types/preview`, form);
  return res.data.data;
};

// After any "delete the affected classes" save (leave, working hours, class
// assignment) the calendar, schedule detail, waitlist and notification views
// must drop the deleted classes: the same keys the delete-schedule mutations
// (schedule-v2.ts) invalidate.
const invalidateAfterScheduleRemoval = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: ["schedule-v2"] });
  queryClient.invalidateQueries({ queryKey: ["schedule"] });
  queryClient.invalidateQueries({ queryKey: ["waitlist-v2"] });
  queryClient.invalidateQueries({ queryKey: ["notification"] });
  // The removed classes refund their bookings; the Klien list counts credits.
  invalidateCustomerList(queryClient);
};

const removalToast = (key: "removedAndApplied" | "removedAndRestored", removal: LeaveRemovalResult) =>
  getApiSuccessMessage(
    translateWithVars(`leave.conflict.${key}`, {
      classes: removal.removed_schedules,
      bookings: removal.refunded_bookings,
    }),
  );

const useApplyLeaveRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (leaveForm: ApplyLeaveForm) => {
      return client.post(`/v1/staff/leave`, leaveForm);
    },
    onSuccess: ({ data: response }) => {
      const staff_id = response.data.staff_id;
      const removal: LeaveRemovalResult | null = response.data.removal ?? null;
      // Invalidate all queries based on their access type
      queryClient.invalidateQueries({ queryKey: [`user`, staff_id] });
      invalidateEmployeeLists(queryClient);
      if (removal) {
        invalidateAfterScheduleRemoval(queryClient);
        removalToast("removedAndApplied", removal);
        return;
      }
      getApiSuccessMessage(
        `${translate("tabs.time_off")} ${translate("messages.success.create")}`,
      );
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      getApiErrorMessage(error);
    },
  });
};

// staff_id travels alongside leave_id purely for cache invalidation on the
// frontend — the backend only needs leave_id.
interface CancelLeaveForm {
  leave_id: string;
  staff_id: string;
  // Restore only: "Hapus Jadwal & Pulihkan Cuti" (see ApplyLeaveForm).
  remove_affected_schedules?: boolean;
}

const useCancelLeave = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ leave_id }: CancelLeaveForm) => {
      return client.put(`/v1/staff/leave/cancel`, { leave_id });
    },
    onSuccess: (_res, form) => {
      queryClient.invalidateQueries({ queryKey: ["user", form.staff_id] });
      invalidateEmployeeLists(queryClient);
      getApiSuccessMessage(translate("messages.success.cancel"));
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      getApiErrorMessage(error);
    },
  });
};

// Undo for an accidental cancel. Leave only has two statuses, so this is
// just flipping back to Approved (see restoreLeaveById, staff.ts) — no
// re-validation against schedules created in the meantime, matching how the
// hard-block itself is a create-time guard, never retroactively re-checked.
const useRestoreLeave = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ leave_id, remove_affected_schedules }: CancelLeaveForm) => {
      return client.put(`/v1/staff/leave/restore`, { leave_id, remove_affected_schedules });
    },
    onSuccess: ({ data: response }, form) => {
      const removal: LeaveRemovalResult | null = response.data.removal ?? null;
      queryClient.invalidateQueries({ queryKey: ["user", form.staff_id] });
      invalidateEmployeeLists(queryClient);
      if (removal) {
        invalidateAfterScheduleRemoval(queryClient);
        removalToast("removedAndRestored", removal);
        return;
      }
      getApiSuccessMessage(translate("messages.success.reactivate"));
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      getApiErrorMessage(error);
    },
  });
};

// Employee management: the weekly working-hours editor always sends its full
// 7-row state, so the same invalidation as useApplyLeaveRequest (the staff
// detail fetch nests working_hour) picks up the change without a dedicated
// read endpoint.
// Toast for a rule save that also deleted classes ("Hapus Kelas & Simpan").
// Zero parts are left out; a removal that deleted nothing (every listed
// class had already started) falls back to the plain "saved" toast.
const ruleRemovalToast = (removal: RuleRemovalResult | null): boolean => {
  if (!removal) return false;
  const key = (leaf: string) => `class_settings.employee_management.${leaf}`;
  const deleted: string[] = [];
  if (removal.removed_series > 0) {
    deleted.push(translateWithVars(key("removal_series"), { count: removal.removed_series }));
  }
  if (removal.removed_occurrences > 0) {
    deleted.push(translateWithVars(key("removal_occurrences"), { count: removal.removed_occurrences }));
  }
  if (deleted.length === 0) return false;
  const bookings =
    removal.refunded_bookings > 0
      ? translateWithVars(key("removal_bookings"), { count: removal.refunded_bookings })
      : "";
  getApiSuccessMessage(
    translateWithVars(key("removal_saved"), {
      deleted: deleted.join(` ${translate(key("removal_join"))} `),
      bookings,
    }),
  );
  return true;
};

const useSetWorkingHours = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (form: SetWorkingHoursForm) => {
      return client.put(`/v1/staff/working-hours`, form);
    },
    onSuccess: ({ data: response }, form) => {
      // `data` is the bare row array (unchanged); the counts ride in `meta`.
      const removal: RuleRemovalResult | null = response.meta?.removal ?? null;
      queryClient.invalidateQueries({ queryKey: ["user", form.staff_id] });
      invalidateEmployeeLists(queryClient);
      if (removal) invalidateAfterScheduleRemoval(queryClient);
      if (!ruleRemovalToast(removal)) getApiSuccessMessage(translate("messages.success.saved"));
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      getApiErrorMessage(error);
    },
  });
};

// Replace-all: sends the instructor's complete assigned class-type set.
const useSetStaffClassTypes = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (form: SetStaffClassTypesForm) => {
      return client.put(`/v1/staff/class-types`, form);
    },
    onSuccess: ({ data: response }, form) => {
      const removal: RuleRemovalResult | null = response.meta?.removal ?? null;
      queryClient.invalidateQueries({ queryKey: ["user", form.staff_id] });
      invalidateEmployeeLists(queryClient);
      if (removal) invalidateAfterScheduleRemoval(queryClient);
      if (!ruleRemovalToast(removal)) getApiSuccessMessage(translate("messages.success.saved"));
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      getApiErrorMessage(error);
    },
  });
};

// Replace an instructor's magic login link with a fresh one (the old link
// stops working immediately). Invalidates the login-code query so the profile
// card shows the new link without a reload.
const useRegenerateInstructorLoginLink = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (staffId: string) => {
      return client.post(`/v1/staff/${staffId}/login-link/regenerate`);
    },
    onSuccess: (_res, staffId) => {
      queryClient.invalidateQueries({
        queryKey: ["staff", staffId, "login-code"],
      });
      getApiSuccessMessage(
        translate("messages.success.regenerate_login_link")
      );
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      getApiErrorMessage(error);
    },
  });
};

// Replace an instructor's 6-char login code with a fresh one. Codes are stored
// per surface, so this never touches the same person's CLIENT code if they also
// book classes here — which is what makes rotating a leaked code safe to offer.
const useRegenerateInstructorLoginCode = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (staffId: string) => {
      return client.post(`/v1/staff/${staffId}/login-code/regenerate`);
    },
    onSuccess: (_res, staffId) => {
      queryClient.invalidateQueries({
        queryKey: ["staff", staffId, "login-code"],
      });
      getApiSuccessMessage(
        translate("messages.success.regenerate_login_code")
      );
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      getApiErrorMessage(error);
    },
  });
};

export {
  getLeaveConflictPreview,
  getEmployeeManagementReactivationPreview,
  getWorkingHoursConflictPreview,
  getClassAssignmentConflictPreview,
  useApplyLeaveRequest,
  useCancelLeave,
  useRestoreLeave,
  useSetWorkingHours,
  useSetStaffClassTypes,
  useRegenerateInstructorLoginLink,
  useRegenerateInstructorLoginCode,
};
